/**
 * SOAP encounter acknowledgment.
 * The guardian acknowledgment is of the encounter. Clinical corrections stay on the same note.
 * Evaluation and intake consent are outside this decision.
 */

export const CLINICAL_REVISION_ADDENDUM = 'CLINICAL_REVISION';
export const SUPERSEDED_GUARDIAN_ADDENDUM = 'SUPERSEDED_GUARDIAN_SIGNATURE';

export const FINALIZED_DOCUMENT_LOCK_MESSAGE =
  'Finalized clinical documents are legally binding records and locked from modification.';

export const ENCOUNTER_FACTS_LOCKED_CODE = 'ENCOUNTER_FACTS_LOCKED';

const NOTE_FACT_KEYS = ['dos', 'time_in', 'time_out', 'duration_minutes', 'clients_treated_together'] as const;
const SERVICE_FACT_KEYS = [
  'client_id',
  'dos',
  'start_time',
  'end_time',
  'duration_minutes',
  'actual_duration_minutes',
  'clients_treated_together'
] as const;

const CLINICAL_FIELD_LABELS: Record<string, string> = {
  subjective: 'narrative',
  objective: 'objective',
  assessment: 'assessment',
  plan: 'plan',
  medical_necessity: 'medical necessity',
  goals_addressed: 'goal performance',
  recommendations: 'recommendations',
  section_data: 'clinical section'
};

export function isSoapDocument(docType: unknown): boolean {
  return String(docType || '').trim().toUpperCase() === 'SOAP';
}

export function documentHasGuardianAcknowledgment(doc: {
  guardian_signature?: unknown;
  guardian_signature_status?: unknown;
  guardian_signed_at?: unknown;
} | null | undefined): boolean {
  return Boolean(
    String(doc?.guardian_signature || '').trim() ||
    String(doc?.guardian_signature_status || '').trim().toUpperCase() === 'SIGNED' ||
    String(doc?.guardian_signed_at || '').trim()
  );
}

export function finalizedClinicalDocumentIsLocked(status: unknown): boolean {
  return String(status || '').trim().toUpperCase() === 'FINALIZED';
}

function parseObject(value: unknown): Record<string, unknown> | null {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'object' && !Array.isArray(value)) return { ...(value as Record<string, unknown>) };
  try {
    const parsed = JSON.parse(String(value));
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
  } catch {
    return null;
  }
  return null;
}

export function parseAddenda(value: unknown): Array<Record<string, unknown>> {
  if (!value) return [];
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return Array.isArray(parsed) ? parsed.filter((entry) => entry && typeof entry === 'object') : [];
  } catch {
    return [];
  }
}

function normFact(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  const text = String(value).trim();
  if (/^-?\d+(\.\d+)?$/.test(text)) {
    const numeric = Number(text);
    if (Number.isFinite(numeric)) return String(numeric);
  }
  return text;
}

function text(value: unknown): string {
  return String(value ?? '').trim();
}

function clip(value: unknown): string {
  const raw = text(value);
  return raw.length > 400 ? `${raw.slice(0, 400)}…` : raw;
}

export function soapHasClinicalRevision(doc: { addenda_json?: unknown } | null | undefined): boolean {
  return parseAddenda(doc?.addenda_json).some((entry) => entry.type === CLINICAL_REVISION_ADDENDUM);
}

export function clinicalRevisionFields(doc: { addenda_json?: unknown } | null | undefined): string[] {
  const fields: string[] = [];
  for (const entry of parseAddenda(doc?.addenda_json)) {
    if (entry.type !== CLINICAL_REVISION_ADDENDUM || !Array.isArray(entry.changed_fields)) continue;
    for (const field of entry.changed_fields) {
      const label = CLINICAL_FIELD_LABELS[String(field)] || String(field);
      if (!fields.includes(label)) fields.push(label);
    }
  }
  return fields;
}

export function guardianAcknowledgmentWasCleared(doc: {
  guardian_signature?: unknown;
  guardian_signed_at?: unknown;
  addenda_json?: unknown;
} | null | undefined): boolean {
  if (text(doc?.guardian_signature) || text(doc?.guardian_signed_at)) return false;
  return parseAddenda(doc?.addenda_json).some((entry) => entry.type === SUPERSEDED_GUARDIAN_ADDENDUM);
}

export function encounterClockFromSources(
  doc: { dos?: unknown; start_time?: unknown; end_time?: unknown; section_data_json?: unknown } | null | undefined,
  service?: { dos?: unknown; start_time?: unknown; end_time?: unknown } | null
): { date: string; timeIn: string; timeOut: string } {
  const section = parseObject(doc?.section_data_json) || {};
  return {
    date: text(service?.dos || doc?.dos || section.dos),
    timeIn: text(service?.start_time || doc?.start_time || section.time_in),
    timeOut: text(service?.end_time || doc?.end_time || section.time_out)
  };
}

export function inPersonEncounterAcknowledgment(clock: { date?: unknown; timeIn?: unknown; timeOut?: unknown }): string {
  const date = text(clock.date);
  const timeIn = text(clock.timeIn);
  const timeOut = text(clock.timeOut);
  const when = date
    ? (timeIn && timeOut ? `on ${date} from ${timeIn} to ${timeOut}` : `on ${date}. Start and stop times have not both been entered`)
    : 'on a date that has not been entered';
  return `By signing, the guardian or designee acknowledges that this encounter occurred ${when}. This acknowledgment is of the encounter. It is not an approval of the clinical narrative.`;
}

export function soapSessionRemoteMessage(clock: { date?: unknown; timeIn?: unknown; timeOut?: unknown }): string {
  const date = text(clock.date) || 'a date that was not stored';
  const timeIn = text(clock.timeIn);
  const timeOut = text(clock.timeOut);
  const times = timeIn && timeOut
    ? `from ${timeIn} to ${timeOut}`
    : 'Start and stop times were not both stored';
  return `By signing, you acknowledge that this encounter occurred on ${date} ${times}. This acknowledgment is of the encounter. It is not an approval of the clinical narrative.`;
}

export function originalAcknowledgmentStatement(clock: { date?: unknown; timeIn?: unknown; timeOut?: unknown }): string {
  const date = text(clock.date) || 'the stored encounter date';
  const timeIn = text(clock.timeIn);
  const timeOut = text(clock.timeOut);
  const times = timeIn && timeOut ? ` from ${timeIn} to ${timeOut}` : '';
  return `Original encounter acknowledgment for the encounter on ${date}${times}. This acknowledgment is of the encounter, not an approval of the clinical narrative. The clinical text is the current revision.`;
}

function different(incoming: unknown, current: unknown): boolean {
  if (incoming === undefined) return false;
  const left = incoming === null ? '' : String(incoming).trim();
  const right = current === null || current === undefined ? '' : String(current).trim();
  return left !== right;
}

export function changedClinicalFields(existing: Record<string, unknown>, incoming: Record<string, unknown>): string[] {
  const fields: string[] = [];
  const pairs: Array<[string, string]> = [
    ['subjective', 'subjective'],
    ['objective', 'objective'],
    ['assessment', 'assessment'],
    ['plan', 'plan'],
    ['medical_necessity', 'medical_necessity'],
    ['goals_addressed_json', 'goals_addressed'],
    ['eval_scores_json', 'eval_scores'],
    ['poc_start_date', 'poc_start_date'],
    ['poc_end_date', 'poc_end_date'],
    ['qpr_quarter', 'qpr_quarter'],
    ['recommendations', 'recommendations']
  ];
  for (const [key, label] of pairs) {
    if (different(incoming[key], existing[key])) fields.push(label);
  }
  if (incoming.section_data_json !== undefined) {
    const incomingJson = typeof incoming.section_data_json === 'object'
      ? JSON.stringify(incoming.section_data_json)
      : incoming.section_data_json;
    if (different(incomingJson, existing.section_data_json)) fields.push('section_data');
  }
  return fields;
}

export function soapNoteFactChanges(existing: Record<string, unknown>, incoming: Record<string, unknown>): string[] | { unreadable: true } {
  if (!isSoapDocument(existing.doc_type) || !documentHasGuardianAcknowledgment(existing)) return [];
  const changes: string[] = [];
  if (incoming.client_id !== undefined && normFact(incoming.client_id) !== normFact(existing.client_id)) {
    changes.push('client');
  }
  if (incoming.section_data_json === undefined) return changes;
  const incomingSection = parseObject(incoming.section_data_json);
  if (!incomingSection) return { unreadable: true };
  const storedSection = parseObject(existing.section_data_json) || {};
  for (const key of NOTE_FACT_KEYS) {
    const before = normFact(storedSection[key]);
    const after = normFact(Object.prototype.hasOwnProperty.call(incomingSection, key) ? incomingSection[key] : '');
    if (before !== after) changes.push(key);
  }
  return changes;
}

export function serviceFactChanges(existing: Record<string, unknown>, incoming: Record<string, unknown>): string[] {
  const changes: string[] = [];
  for (const key of SERVICE_FACT_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(incoming, key) || incoming[key] === undefined) continue;
    if (incoming[key] === null || incoming[key] === '') continue;
    if (normFact(incoming[key]) !== normFact(existing[key])) changes.push(key === 'client_id' ? 'client' : key);
  }
  return changes;
}

export function acknowledgedSoapOnService(documents: Array<Record<string, unknown>> | null | undefined): boolean {
  return (documents || []).some((doc) => isSoapDocument(doc.doc_type) && documentHasGuardianAcknowledgment(doc));
}

function previousClinicalValues(existing: Record<string, unknown>, fields: string[]): Record<string, string> {
  const map: Record<string, string> = {
    narrative: 'subjective',
    subjective: 'subjective',
    objective: 'objective',
    assessment: 'assessment',
    plan: 'plan',
    'medical necessity': 'medical_necessity',
    medical_necessity: 'medical_necessity',
    'goal performance': 'goals_addressed_json',
    goals_addressed: 'goals_addressed_json',
    recommendations: 'recommendations',
    'clinical section': 'section_data_json',
    section_data: 'section_data_json'
  };
  const previous: Record<string, string> = {};
  for (const field of fields) {
    const key = map[field] || field;
    if (existing[key] !== undefined && existing[key] !== null) previous[field] = clip(existing[key]);
  }
  return previous;
}

export interface SoapUpdatePlan {
  locked: boolean;
  reject: boolean;
  status: number;
  error: string;
  code: string;
  fields: string[];
  clearGuardian: boolean;
  preserveAcknowledgment: boolean;
  changedFields: string[];
  addendaJson: string | null;
  guardianSignature: unknown;
  guardianSignedAt: unknown;
  guardianSignatureStatus: unknown;
  esignStatus: unknown;
  documentStatus: unknown;
  supervisorApprovalStatus: unknown;
}

export function planClinicalDocumentUpdate(
  existing: Record<string, unknown>,
  incoming: Record<string, unknown>,
  userId: string
): SoapUpdatePlan {
  const supervisorApprovalStatus = existing.supervisor_approval_status === 'RETURNED'
    ? 'PENDING'
    : existing.supervisor_approval_status;
  const base: SoapUpdatePlan = {
    locked: false,
    reject: false,
    status: 200,
    error: '',
    code: '',
    fields: [],
    clearGuardian: false,
    preserveAcknowledgment: false,
    changedFields: [],
    addendaJson: existing.addenda_json == null ? null : String(existing.addenda_json),
    guardianSignature: existing.guardian_signature ?? null,
    guardianSignedAt: existing.guardian_signed_at ?? null,
    guardianSignatureStatus: existing.guardian_signature_status ?? null,
    esignStatus: existing.esign_status ?? null,
    documentStatus: existing.status,
    supervisorApprovalStatus
  };

  if (finalizedClinicalDocumentIsLocked(existing.status)) {
    return {
      ...base,
      locked: true,
      status: 400,
      error: FINALIZED_DOCUMENT_LOCK_MESSAGE
    };
  }

  const factChanges = soapNoteFactChanges(existing, incoming);
  if (!Array.isArray(factChanges)) {
    return {
      ...base,
      reject: true,
      status: 422,
      code: ENCOUNTER_FACTS_LOCKED_CODE,
      error: 'The clinical section could not be read, so the acknowledged encounter was not changed.',
      fields: ['section_data']
    };
  }
  if (factChanges.length > 0) {
    return {
      ...base,
      reject: true,
      status: 422,
      code: ENCOUNTER_FACTS_LOCKED_CODE,
      error: 'This encounter is already acknowledged. Client, service date, start and stop times, duration, and clients treated together cannot be changed on this note.',
      fields: factChanges
    };
  }

  const changedFields = changedClinicalFields(existing, incoming);
  base.changedFields = changedFields;
  const acknowledged = documentHasGuardianAcknowledgment(existing);
  if (!acknowledged || changedFields.length === 0) return base;

  if (isSoapDocument(existing.doc_type)) {
    const addenda = parseAddenda(existing.addenda_json);
    addenda.push({
      type: CLINICAL_REVISION_ADDENDUM,
      version: Number(existing.version || 1) + 1,
      revised_at: new Date().toISOString(),
      revised_by: userId || '',
      changed_fields: changedFields,
      previous_values: previousClinicalValues(existing, changedFields),
      note: 'Clinical content was revised after the encounter acknowledgment. The original guardian acknowledgment was kept.'
    });
    return {
      ...base,
      preserveAcknowledgment: true,
      addendaJson: JSON.stringify(addenda)
    };
  }

  const addenda = parseAddenda(existing.addenda_json);
  addenda.push({
    type: SUPERSEDED_GUARDIAN_ADDENDUM,
    superseded_version: existing.version || 1,
    guardian_signature: existing.guardian_signature,
    guardian_signed_at: existing.guardian_signed_at,
    guardian_signature_status: existing.guardian_signature_status,
    guardian_relationship: existing.guardian_relationship,
    esign_envelope_id: existing.esign_envelope_id,
    esign_completed_at: existing.esign_completed_at,
    invalidated_at: new Date().toISOString(),
    invalidated_by: userId || '',
    invalidation_reason: 'Material clinical content revised after guardian signature',
    changed_material_fields: changedFields
  });
  return {
    ...base,
    clearGuardian: true,
    addendaJson: JSON.stringify(addenda),
    guardianSignature: null,
    guardianSignedAt: null,
    guardianSignatureStatus: 'PENDING',
    esignStatus: 'NOT_REQUESTED',
    documentStatus: 'PENDING_SIGNATURES'
  };
}

export function serviceEncounterFactRejection(
  service: Record<string, unknown>,
  incoming: Record<string, unknown>,
  documents: Array<Record<string, unknown>>
): { status: number; error: string; code: string; fields: string[] } | null {
  if (!acknowledgedSoapOnService(documents)) return null;
  const fields = serviceFactChanges(service, incoming);
  if (fields.length === 0) return null;
  return {
    status: 422,
    code: ENCOUNTER_FACTS_LOCKED_CODE,
    error: 'This encounter is already acknowledged. Client, service date, start and stop times, duration, and clients treated together cannot be changed on this visit.',
    fields
  };
}

export interface SoapCompletionDecision {
  skip: boolean;
  reason: 'unrelated_envelope' | 'previously_cleared' | 'preserve_original_time' | 'first_completion';
  signedAt: string | null;
}

/** Repeated or delayed SOAP completion must not move the original signing time or refill a cleared acknowledgment. */
export function soapDelayedCompletion(doc: Record<string, unknown>, eventEnvelopeId: string): SoapCompletionDecision {
  if (!isSoapDocument(doc.doc_type)) {
    return { skip: false, reason: 'first_completion', signedAt: null };
  }
  const activeEnvelope = text(doc.esign_envelope_id);
  if (activeEnvelope && activeEnvelope !== text(eventEnvelopeId)) {
    return { skip: true, reason: 'unrelated_envelope', signedAt: text(doc.guardian_signed_at) || null };
  }
  if (guardianAcknowledgmentWasCleared(doc)) {
    return { skip: true, reason: 'previously_cleared', signedAt: null };
  }
  const signedAt = text(doc.guardian_signed_at);
  if (signedAt) return { skip: false, reason: 'preserve_original_time', signedAt };
  return { skip: false, reason: 'first_completion', signedAt: null };
}

export function soapRevisionDisplay(doc: {
  addenda_json?: unknown;
  dos?: unknown;
  start_time?: unknown;
  end_time?: unknown;
  section_data_json?: unknown;
  guardian_signed_at?: unknown;
} | null | undefined): {
  revised: boolean;
  narrativeTitle: string;
  guardianTitle: string;
  statement: string;
  fields: string[];
} {
  const revised = soapHasClinicalRevision(doc);
  const clock = encounterClockFromSources(doc);
  return {
    revised,
    narrativeTitle: revised ? 'Current clinical revision' : 'Session narrative',
    guardianTitle: revised ? 'Original encounter acknowledgment' : 'Guardian / signer',
    statement: revised ? originalAcknowledgmentStatement(clock) : '',
    fields: revised ? clinicalRevisionFields(doc) : []
  };
}

export function keepStoredSoapFileWithRevision(doc: { doc_type?: unknown; addenda_json?: unknown } | null | undefined): boolean {
  return isSoapDocument(doc?.doc_type) && soapHasClinicalRevision(doc);
}
